import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';

import { RateCellRender } from '../RateCellRender';
import { twoDecimalRateFormatter } from '../tableCustomCellRender';
import type { SourceColumn } from '../useDashboardColumns';
import type { RecReviewData } from '../useDashboardController';

import { labels } from 'locales/en.label';
import { ColumnSources } from './types';

export const ratesColsData: SourceColumn[] = [
	{
		headerName: 'Prev\nBWH Rate',
		field: 'last_current_rate',
		headerTooltip: labels.rec_review.dashboard.tooltip.last_current_rate,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Prev\nRec Rate',
		field: 'prev_rec_rate',
		headerTooltip: labels.rec_review.dashboard.tooltip.prev_rec_rate,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Rate\nVariance',
		field: 'rate_variance',
		headerTooltip: labels.rec_review.dashboard.tooltip.rate_variance,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Rate Varience\n(Rec vs BWH)',
		field: 'rec_current_variance',
		headerTooltip: labels.rec_review.dashboard.tooltip.rec_current_variance,
		valueGetter: (params: ValueGetterParams<RecReviewData>) => {
			const data = params.data;
			if (!data) return null;
			const recRate = data.rec_rate;
			const currentRate = data.current_rate_live ?? data.current_rate;
			if (recRate == null || currentRate == null) return null;
			return recRate - currentRate;
		},
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Yoy Rate\n(LYST)',
		field: 'yoy_rate_lyst',
		headerTooltip: labels.rec_review.dashboard.tooltip.yoy_rate_lyst,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Yoy Rate\n(LYF)',
		field: 'yoy_rate_lyf',
		headerTooltip: labels.rec_review.dashboard.tooltip.yoy_rate_lyf,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Unbounded\nRec Rate',
		field: 'unbounded_rec_rate',
		headerTooltip: 'Unbounded recommended rate',
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Derived\nRate',
		field: 'derived_rate',
		headerTooltip: labels.rec_review.dashboard.tooltip.derived_rate,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Conservative\nRate',
		field: 'conservative_rate',
		headerTooltip: 'Conservative rate recommendation',
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Neutral\nRate',
		field: 'neutral_rate',
		headerTooltip: 'Neutral rate recommendation',
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
	{
		headerName: 'Aggressive\nRate',
		field: 'aggressive_rate',
		headerTooltip: 'Aggressive rate recommendation',
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.RATES,
	},
];
